import React, {useState, useEffect, useCallback} from 'react';
import _ from 'lodash';
import classnames from 'classnames/bind';
import analyseData from '../../../utils/analyseSearchData';
import Filter from '../../../utils/filter';
import {
  ISearchVehicle as IVehicle,
  IVehicleType,
  Replacement,
} from '../../../interface';
import QuickSearchInput from '../QuickSearchInput';
import ToggleFilter from '../../ToggleFilter';
import FilterDropdown from '../../FilterDropdown';
import FilterResult from '../../FilterResult';
import style from './index.module.scss';
import instance from '../../../services/httpRequest';
import Loading from '../../Loading';
import localesReplacement from '../../../utils/localesReplacement';
import {useDebounce} from 'use-debounce';
import {INIT_FILTER_DROPDOWN} from '../../../constants';

export interface IProps {
  country?: string;
  language: string | undefined;
  replacements: Replacement;
  onVehicleSelect?: (
    value: string | number,
    model: string,
    vehicleType?: string,
  ) => void;
  switchVehicleSearch: () => void;
  licence: boolean;
  licensePlateRegexes: string;
  selectedWorkshop: boolean;
  restore?: boolean;
  preserved?: boolean;
  userType?: boolean;
  captcha: boolean;
  isExtendedRestApi: boolean;
}

const cx = classnames.bind(style);
const classDropdownItem = 'dropdown-item';
interface IFilterCategories {
  categoriesTypes: string[];
  makeTypes: string[];
  modelTypes: string[];
  yearTypes: string[];
}

function quickSearch(
  value: string,
  isPersonalizedLicensePlate: boolean,
  userType = false,
  country?: string,
  signal?: AbortSignal,
  category?: string,
  make?: string,
  model?: string,
  year?: string,
) {
  const window: any = globalThis;
  return instance
    .get(
      `/quickSearch?text=${encodeURIComponent(value)}${
        isPersonalizedLicensePlate ? '&isPersonalizedLicensePlate=true' : ''
      }${category && '&categories=' + category}${make && '&makes=' + make}${
        model && '&models=' + model
      }${year && '&years=' + year}`,
      {
        signal: signal,
      },
    )
    .then((d) => {
      const event =
        d.data?.length > 0
          ? 'quickSearches'
          : 'detailsUnavailable_quickSearches';
      const searchQuery =
        d.data?.length > 0 && !d.data[0]?.model
          ? 'licensePlateSearch'
          : encodeURIComponent(value);

      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: event,
        searchType: 'quick search',
        pageType: 'product finder',
        searchQuery: searchQuery,
        userType: userType ? 'workshop owner' : 'vehicle owner',
        countryCode: country,
      });

      return d;
    });
}

function QuickSearch(props: IProps) {
  const {
    onVehicleSelect,
    replacements,
    country,
    switchVehicleSearch,
    licence,
    licensePlateRegexes,
    selectedWorkshop,
    restore,
    preserved,
    userType,
    captcha,
    language,
    isExtendedRestApi,
  } = props;
  const [filterVisible, setFilterVisible] = useState<boolean>(false);
  const [vehicleTypes, setVehicleTypes] = useState<IVehicleType[]>([]);
  const [vehicleType, setVehicleType] = useState<string>('');
  const [filterTypeValues, setFilterTypeValues] =
    useState<IFilterCategories>(INIT_FILTER_DROPDOWN);
  const [make, setMake] = useState<string>('');
  const [model, setModel] = useState<string>('');
  const [year, setYear] = useState<string>('');
  const [searchKey, setSearchKey] = useState<string>('');
  const [results, setResults] = useState<IVehicle[]>([]);
  const [isMount, setIsMount] = useState<boolean>(false);
  const [finish, setFinish] = useState<boolean>(false);
  const [isPersonalizedLicensePlate, setIsPersonalizedLicensePlate] =
    useState(false);
  const controller = new AbortController();
  const regExp = new RegExp(licensePlateRegexes).test(searchKey);
  const [debounceKey] = useDebounce(searchKey, 1000);
  //Get licence search params from API
  useEffect(() => {
    return () => {
      _.unset(instance.defaults.headers, 'Authorization');
      setResults([]);
    };
  }, []);
  useEffect(() => {
    memoAutocompleted(debounceKey);
  }, [debounceKey]);

  /* Quick search input change & clear function */
  const changeHandler = (value: string, token?: string) => {
    if (captcha && licence) {
      if (token) {
        _.assign(instance.defaults.headers, {
          Authorization: `Captcha ${token}`,
        });
      } else {
        _.unset(instance.defaults.headers, 'Authorization');
        setResults([]);
        return;
      }
    }
    if (_.trim(value).length >= 2) {
      setIsMount(true);
    }
    setSearchKey(value);
    restore && sessionStorage.setItem('searchKey', value);
    setIsPersonalizedLicensePlate(false);
  };

  const getQuickSearchData = (
    value: string,
    isPersonalizedLicensePlate: boolean,
    vehicleType: string,
    make: string,
    model: string,
    year: string,
  ) => {
    quickSearch(
      value,
      isPersonalizedLicensePlate,
      userType,
      country,
      controller.signal,
      vehicleType,
      make,
      model,
      year,
    ).then((d) => {
      if (_.inRange(d.status, 200, 300)) {
        if (isExtendedRestApi) {
          setVehicleTypes(analyseData.vehicleTypeFormat(d.data?.results));
          setResults(d.data?.results);
          setFilterTypeValues((prev) => {
            return {
              ...prev,
              makeTypes: analyseData.getValues(d.data?.makes, true) ?? [],
              modelTypes: analyseData.getValues(d.data?.models, true) ?? [],
              categoriesTypes:
                analyseData.getValues(d.data?.categories, true) ?? [],
              yearTypes: analyseData.getValues(d.data?.years, true) ?? [],
            };
          });
        } else {
          setVehicleTypes(analyseData.vehicleTypeFormat(d.data));
          setResults(d.data);
        }
      }
      setIsMount(false);
      setFinish(true);
    });
  };

  const memoAutocompleted = useCallback(
    (
      value: string,
      isPersonalizedLicensePlate = false,
      vehicleType = '',
      make = '',
      model = '',
      year = '',
    ) => {
      if (_.trim(value).length >= 2) {
        setFinish(false);
        setIsMount(true);
        selectedWorkshop
          ? _.assign(instance.defaults.headers, {
              isWorkshopOwner: selectedWorkshop,
            })
          : _.unset(instance.defaults.headers, 'isWorkshopOwner');
        getQuickSearchData(
          value,
          isPersonalizedLicensePlate,
          vehicleType,
          make,
          model,
          year,
        );
      } else {
        setIsMount(false);
        setFinish(false);
        setFilterVisible(false);
        setResults([]);
        setFilterTypeValues(INIT_FILTER_DROPDOWN);
      }
      setVehicleType('');
      setMake('');
      setModel('');
      setYear('');
      setFilterTypeValues(INIT_FILTER_DROPDOWN);
    },
    [selectedWorkshop, isExtendedRestApi],
  );
  const clearQuickSearch = () => {
    setSearchKey('');
    memoAutocompleted('');
  };
  /* Vehicle Type dropdown change */
  const changeVehicleType = (vehicleType: string) => {
    setVehicleType(vehicleType);
    setMake('');
    setModel('');
    setYear('');
    isExtendedRestApi &&
      memoAutocompleted(
        debounceKey,
        isPersonalizedLicensePlate,
        vehicleType,
        make,
        model,
        year,
      );
    restore && sessionStorage.setItem('searchQType', vehicleType);
  };
  /* Manufacturer dropdown change */
  const changeMake = (make: string) => {
    setMake(make);
    setModel('');
    setYear('');
    isExtendedRestApi &&
      memoAutocompleted(
        debounceKey,
        isPersonalizedLicensePlate,
        vehicleType,
        make,
        model,
        year,
      );
    restore && sessionStorage.setItem('searchQType', vehicleType);
  };
  /* Model dropdown change */
  const changeModel = (model: string) => {
    setModel(model);
    setYear('');
    isExtendedRestApi &&
      memoAutocompleted(
        debounceKey,
        isPersonalizedLicensePlate,
        vehicleType,
        make,
        model,
        year,
      );
    restore && sessionStorage.setItem('searchQType', vehicleType);
  };
  /* Year dropdown change */
  const changeYear = (year: string) => {
    setYear(year);
    isExtendedRestApi &&
      memoAutocompleted(
        debounceKey,
        isPersonalizedLicensePlate,
        vehicleType,
        make,
        model,
        year,
      );
    restore && sessionStorage.setItem('searchQYear', year);
  };
  const searchByPlate = () => {
    memoAutocompleted(searchKey, true);
    setIsPersonalizedLicensePlate(true);
  };

  useEffect(() => {
    memoAutocompleted(searchKey);
    setResults([]);
    setVehicleType('');
    setMake('');
    setModel('');
    setYear('');
  }, [selectedWorkshop]);

  useEffect(() => {
    if (preserved) {
      setSearchKey(sessionStorage.getItem('searchKey') ?? '');
    }
  }, [preserved]);
  useEffect(() => {
    if (preserved && results) {
      setVehicleType(sessionStorage.getItem('searchQType') ?? '');
      setMake(sessionStorage.getItem('searchQMake') ?? '');
      setModel(sessionStorage.getItem('searchQmodel') ?? '');
      setYear(sessionStorage.getItem('searchQYear') ?? '');
    }
  }, [preserved, results]);

  const SEARCHFORNUMBERPLATE = (
    <div className='guide'>
      <div className='infor'>
        <p>
          {localesReplacement(replacements, 'No results found for')}
          <strong>{_.trim(searchKey)}</strong>
        </p>
      </div>
      <div className='guide-block'>
        <p>
          {localesReplacement(replacements, 'If')}
          <strong>{_.trim(searchKey)}</strong>
          {localesReplacement(
            replacements,
            'is a personalised license plate, try clicking Search by Number plate below.',
          )}
        </p>
        <div className='buttons'>
          <button className='primary' onClick={searchByPlate}>
            {localesReplacement(replacements, 'Search by the Number plate')}
          </button>
          <button onClick={clearQuickSearch}>
            {localesReplacement(replacements, 'Start New Search')}
          </button>
        </div>
      </div>
    </div>
  );

  const EMPTYFORNUMBERPLATE = (
    <div className='guide guide-empty'>
      <div className='infor'>
        <p>
          {localesReplacement(replacements, 'No results found for')}
          <strong>{_.trim(searchKey)}</strong>
          {localesReplacement(
            replacements,
            '. Try searching using the Vehicle Search',
          )}
        </p>
      </div>
      <div className='guide-block'>
        <div className='buttons'>
          <button className='primary' onClick={switchVehicleSearch}>
            {localesReplacement(replacements, 'Vehicle Search')}
          </button>
          <button onClick={clearQuickSearch}>
            {localesReplacement(replacements, 'Start New Search')}
          </button>
        </div>
      </div>
    </div>
  );

  const renderNoMatch = () =>
    finish &&
    _.trim(searchKey).length >= 3 &&
    (!_.isEqual(country, 'USA') && licence ? (
      <div className='no-match'>
        {isPersonalizedLicensePlate || regExp
          ? EMPTYFORNUMBERPLATE
          : SEARCHFORNUMBERPLATE}
      </div>
    ) : (
      <div className='no-match'>{EMPTYFORNUMBERPLATE}</div>
    ));

  useEffect(() => {
    if (isExtendedRestApi && !!filterTypeValues) {
      if (filterTypeValues.categoriesTypes.length === 1) {
        setVehicleType(filterTypeValues.categoriesTypes[0]);
      } else if (filterTypeValues.makeTypes.length === 1) {
        setMake(filterTypeValues.makeTypes[0]);
      } else if (filterTypeValues.modelTypes.length === 1) {
        setModel(filterTypeValues.modelTypes[0]);
      } else if (filterTypeValues.yearTypes.length === 1) {
        setYear(filterTypeValues.yearTypes[0]);
      }
    }
  }, [filterTypeValues, isExtendedRestApi]);

  const vehicleTypeDataList = isExtendedRestApi
    ? filterTypeValues?.categoriesTypes ?? []
    : analyseData.vehicleType(vehicleTypes);

  const makeTypeDataList = isExtendedRestApi
    ? filterTypeValues?.makeTypes ?? []
    : vehicleType
    ? analyseData.make(vehicleTypes, vehicleType)
    : analyseData.getMake(vehicleTypes);

  const modeTypeDataList = isExtendedRestApi
    ? filterTypeValues?.modelTypes ?? []
    : analyseData.model(vehicleTypes, vehicleType, make);

  const yearDataList = isExtendedRestApi
    ? filterTypeValues?.yearTypes ?? []
    : analyseData.getYear(
        new Filter(results)
          .vehicleType(vehicleType)
          .manufacturer(make)
          .model(model)
          .value(),
      );

  const renderResults = () => {
    return !_.isEmpty(results) ? (
      <>
        <ToggleFilter
          toggleHandler={setFilterVisible}
          panelVisible={filterVisible}
        />
        <div className={cx('dropdown-list', `${licence ? 'licence' : ''}`)}>
          {results.length > 1 && (
            <>
              {!!vehicleTypes && (
                <>
                  <div
                    className={cx(classDropdownItem, {
                      hidden: !filterVisible,
                    })}
                  >
                    <FilterDropdown
                      dataList={vehicleTypeDataList}
                      filter={
                        filterTypeValues.categoriesTypes.length === 1
                          ? filterTypeValues.categoriesTypes[0]
                          : vehicleType
                      }
                      changeHandler={changeVehicleType}
                      type={localesReplacement(replacements, 'Vehicle Type')}
                      replacements={replacements}
                    />
                  </div>
                  <div
                    className={cx(classDropdownItem, {
                      hidden: !filterVisible,
                    })}
                  >
                    <FilterDropdown
                      dataList={makeTypeDataList}
                      filter={
                        filterTypeValues.makeTypes.length === 1
                          ? filterTypeValues.makeTypes[0]
                          : make
                      }
                      changeHandler={changeMake}
                      type={localesReplacement(replacements, 'Make')}
                      replacements={replacements}
                    />
                  </div>
                  <div
                    className={cx(classDropdownItem, {
                      hidden: !filterVisible,
                    })}
                  >
                    <FilterDropdown
                      dataList={modeTypeDataList}
                      filter={
                        filterTypeValues.modelTypes.length === 1
                          ? filterTypeValues.modelTypes[0]
                          : model
                      }
                      changeHandler={changeModel}
                      type={localesReplacement(replacements, 'Model')}
                      replacements={replacements}
                    />
                  </div>
                </>
              )}
              {!!results && (
                <>
                  <div
                    className={cx(classDropdownItem, {
                      hidden: !filterVisible,
                    })}
                  >
                    <FilterDropdown
                      dataList={yearDataList}
                      filter={
                        filterTypeValues.yearTypes.length === 1
                          ? filterTypeValues.yearTypes[0]
                          : year
                      }
                      changeHandler={changeYear}
                      type={localesReplacement(replacements, 'Year')}
                      replacements={replacements}
                    />
                  </div>
                </>
              )}
            </>
          )}
        </div>
        <FilterResult
          dataList={results}
          vehicleType={vehicleType}
          make={make}
          model={model}
          year={year}
          sort={true}
          value={searchKey}
          onSelect={onVehicleSelect}
          replacements={replacements}
        />
      </>
    ) : (
      renderNoMatch()
    );
  };

  return (
    <>
      <QuickSearchInput
        value={searchKey}
        onChangeHandler={changeHandler}
        onClearHandler={clearQuickSearch}
        licence={licence}
        captcha={captcha}
        replacements={replacements}
        country={country}
        language={language}
      />
      {!isMount ? renderResults() : <Loading />}
    </>
  );
}

export default QuickSearch;
